import {
  PROBLEM_LIFECYCLE_LABELS,
  TICKET_TYPE_LABELS,
  TicketPriority,
  TicketStatus,
  type TicketListQuery,
  type TicketLookups,
} from '@gb-ticket/shared-types';

import { flattenCategories } from '@/products/helpdesk/features/tickets/category-options';

export type ExtraFilterChip = {
  id: string;
  label: string;
  patch: Partial<TicketListQuery>;
};

function without(values: readonly string[] | undefined, drop: string): string[] | undefined {
  const next = (values ?? []).filter((value) => value !== drop);
  return next.length > 0 ? next : undefined;
}

function formatDay(ymd: string): string {
  const [year, month, day] = ymd.split('-');
  if (!year || !month || !day) {
    return ymd;
  }
  return `${day}.${month}.${year}`;
}

export function extraTicketFilterActive(query: TicketListQuery): boolean {
  return (
    (query.type?.length ?? 0) > 0 ||
    query.isMajor === true ||
    (query.problemLifecycle?.length ?? 0) > 0 ||
    (query.categoryId?.length ?? 0) > 0 ||
    (query.queueId?.length ?? 0) > 0 ||
    query.createdFrom != null ||
    query.createdTo != null
  );
}

export function extraTicketFilterChips(
  query: TicketListQuery,
  lookups: TicketLookups | undefined,
): ExtraFilterChip[] {
  const chips: ExtraFilterChip[] = [];
  const categories = flattenCategories(lookups?.categories ?? []);
  const queues = lookups?.queues ?? [];

  for (const type of query.type ?? []) {
    chips.push({
      id: `type:${type}`,
      label: TICKET_TYPE_LABELS[type] ?? type,
      patch: { type: without(query.type, type) as TicketListQuery['type'], page: 1 },
    });
  }

  if (query.isMajor === true) {
    chips.push({
      id: 'isMajor',
      label: 'Nur Major Incidents',
      patch: { isMajor: undefined, page: 1 },
    });
  }

  for (const lifecycle of query.problemLifecycle ?? []) {
    chips.push({
      id: `problemLifecycle:${lifecycle}`,
      label: PROBLEM_LIFECYCLE_LABELS[lifecycle] ?? lifecycle,
      patch: {
        problemLifecycle: without(
          query.problemLifecycle,
          lifecycle,
        ) as TicketListQuery['problemLifecycle'],
        page: 1,
      },
    });
  }

  for (const categoryId of query.categoryId ?? []) {
    chips.push({
      id: `category:${categoryId}`,
      label: categories.find((category) => category.id === categoryId)?.label ?? 'Kategorie',
      patch: { categoryId: without(query.categoryId, categoryId), page: 1 },
    });
  }

  for (const queueId of query.queueId ?? []) {
    chips.push({
      id: `queue:${queueId}`,
      label: queues.find((queue) => queue.id === queueId)?.name ?? 'Warteschlange',
      patch: { queueId: without(query.queueId, queueId), page: 1 },
    });
  }

  if (query.createdFrom) {
    chips.push({
      id: 'createdFrom',
      label: `Von ${formatDay(query.createdFrom)}`,
      patch: { createdFrom: undefined, page: 1 },
    });
  }

  if (query.createdTo) {
    chips.push({
      id: 'createdTo',
      label: `Bis ${formatDay(query.createdTo)}`,
      patch: { createdTo: undefined, page: 1 },
    });
  }

  return chips;
}

export function isMineFilterActive(query: TicketListQuery, userId: string | undefined): boolean {
  return Boolean(
    userId &&
    query.assigneeId?.length === 1 &&
    query.assigneeId[0] === userId &&
    query.unassigned !== true,
  );
}

export function isUnassignedFilterActive(query: TicketListQuery): boolean {
  return query.unassigned === true;
}

export function isWaitingForCustomerFilterActive(query: TicketListQuery): boolean {
  return query.status?.length === 1 && query.status[0] === TicketStatus.WAITING_FOR_CUSTOMER;
}

export function isCriticalFilterActive(query: TicketListQuery): boolean {
  return query.priority?.length === 1 && query.priority[0] === TicketPriority.CRITICAL;
}

export function toggleMineQuery(
  query: TicketListQuery,
  userId: string,
): Pick<TicketListQuery, 'assigneeId' | 'unassigned' | 'page'> {
  if (isMineFilterActive(query, userId)) {
    return { assigneeId: undefined, page: 1 };
  }
  return { assigneeId: [userId], unassigned: undefined, page: 1 };
}

export function toggleUnassignedQuery(
  query: TicketListQuery,
): Pick<TicketListQuery, 'assigneeId' | 'unassigned' | 'page'> {
  if (isUnassignedFilterActive(query)) {
    return { unassigned: undefined, page: 1 };
  }
  return { unassigned: true, assigneeId: undefined, page: 1 };
}

export function toggleWaitingForCustomerQuery(
  query: TicketListQuery,
): Pick<TicketListQuery, 'status' | 'page'> {
  if (isWaitingForCustomerFilterActive(query)) {
    return { status: undefined, page: 1 };
  }
  return { status: [TicketStatus.WAITING_FOR_CUSTOMER], page: 1 };
}

export function toggleCriticalQuery(
  query: TicketListQuery,
): Pick<TicketListQuery, 'priority' | 'page'> {
  if (isCriticalFilterActive(query)) {
    return { priority: undefined, page: 1 };
  }
  return { priority: [TicketPriority.CRITICAL], page: 1 };
}
